import React, { useMemo } from 'react';
import {
  ArrowBackIcon,
  Breadcrumbs,
  ColorBox,
  Grid,
  Link,
  PageHeader,
  Typography
} from '@esgian/esgianui';
import { OperatorProfileSection } from '@components/Sections/ShipAnalytics';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getOperatorProfile, getVesselsPositions } from '@api';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getSegment } from '@store/features';
import { CommercialCategoryTypeSelect } from '@components/Inputs';
import { useSegment, useTheme } from '@hooks';
import { VesselsMap } from '@components/Maps';
import { MAP_STYLE_DEEP_DARK_MODE, MAP_STYLE_LIGHT_MODE } from '@constants';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

const OVERVIEW_MAP_FULLSCREEN_PARAM = 'overviewMapFullscreen';

function OperatorProfilePage() {
  const segment = useSelector(getSegment);
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { isRoRo, uriExt } = useSegment();
  const commercialCategoryType = useSelector(getCommercialCategoryType);

  const { theme } = useTheme();

  const routeChange = (path) => {
    navigate(path);
  };

  const isOverviewMapFullscreen = searchParams.get(OVERVIEW_MAP_FULLSCREEN_PARAM) === 'true';

  const overviewDataQuery = useQuery({
    queryKey: [
      'operatorOverview',
      {
        OperatorId: params.id,
        SegmentTypeId: segment.id,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!segment && !!params.id,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getOperatorProfile(queryKey[1], signal)
        .then((resp) => resp)
        .catch(() => {
          toast.error(`Unable to fetch overview data`, {
            position: 'top-right'
          });
          return null;
        })
  });

  const vesselDataQuery = useQuery({
    queryKey: ['vesselsPosition', overviewDataQuery.data?.operatorVessels?.map(({ imo }) => imo)],
    enabled: !!overviewDataQuery.data?.operatorVessels?.length,
    placeholderData: [],
    queryFn: ({ signal, queryKey }) =>
      getVesselsPositions(queryKey[1], signal)
        .then((resp) => resp)
        .catch(() => {
          toast.error(`Unable to fetch vessels position`, {
            position: 'top-right'
          });
          return null;
        })
  });

  const { operatorLongName, operatorShortName } = overviewDataQuery.data || {};

  const renderedOverviewMap = useMemo(
    () => (
      <VesselsMap
        showSpeedLegend
        loading={vesselDataQuery.isFetching}
        height={isOverviewMapFullscreen ? '98vh' : '50vh'}
        zoom={1}
        projection={'mercator'}
        vessels={vesselDataQuery.data}
        mapStyle={theme.mode === 'dark' ? MAP_STYLE_DEEP_DARK_MODE : MAP_STYLE_LIGHT_MODE}
        isFullscreen={isOverviewMapFullscreen}
        allowFullscreen={true}
      />
    ),
    [vesselDataQuery, theme, isOverviewMapFullscreen]
  );

  if (isOverviewMapFullscreen) {
    return renderedOverviewMap;
  }

  return (
    <Grid container spacing={2}>
      <Grid item>
        <ColorBox onClick={() => routeChange(-1)}>
          <ArrowBackIcon fontSize={'small'} />
        </ColorBox>
      </Grid>
      <Grid item>
        <PageHeader
          loading={overviewDataQuery.isFetching}
          title={`${operatorLongName} (${operatorShortName})`}
          breadcrumbs={
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                underline="hover"
                color="inherit"
                sx={{ cursor: 'pointer' }}
                onClick={() => routeChange(`${uriExt}/commercial-analytics`)}>
                Home
              </Link>
              <Link
                sx={{ cursor: 'pointer' }}
                underline="hover"
                color="inherit"
                onClick={() => routeChange(`${uriExt}/commercial-analytics`)}>
                Commercial Analytics
              </Link>
              <Typography color="primary">Operator Profile</Typography>
            </Breadcrumbs>
          }
        />
      </Grid>
      {!isRoRo && (
        <Grid item xs={12}>
          <CommercialCategoryTypeSelect
            typeCategoryVesselsCount={overviewDataQuery.data?.typeCategoryVesselsCount ?? {}}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <OperatorProfileSection
          overviewDataQuery={overviewDataQuery}
          renderedOverviewMap={renderedOverviewMap}
        />
      </Grid>
    </Grid>
  );
}

OperatorProfilePage.propTypes = {};

OperatorProfilePage.defaultProps = {};

export default OperatorProfilePage;
